import AuctionWidgetV2 from '@/shared/components/AuctionWidgetV2';
import Card from '@/shared/components/flip-ui-kit/Card';
import { type AuctionInfo } from '@/shared/hooks/useAuction';
import useEpochsWithRotation from '@/shared/hooks/useEpochsWithRotation';
import CoinsIcon from '@/shared/icons/flip-ui-kit/small/Coins';
import GavelIcon from '@/shared/icons/flip-ui-kit/small/Gavel';
import MoneyBagIcon from '@/shared/icons/flip-ui-kit/small/MoneyBag';
import NetworkIcon from '@/shared/icons/flip-ui-kit/small/Network';
import { FLIP_SYMBOL } from '@/shared/utils/env';
import { formatUsdValue } from '@/shared/utils/numbers';
import useFlipSupply from '../../../hooks/useFlipSupply';

type AuctionStatPanelsProps = {
  auction: AuctionInfo | null;
  error: Error | undefined;
  isLoading: boolean;
  qualifiedNodes: number;
};

const AuctionStatPanels: React.FC<AuctionStatPanelsProps> = ({
  auction,
  isLoading,
  qualifiedNodes,
}) => {
  const { annualSystemCompoundedRewards, annualSystemCompoundedRewardsValueUsd } = useFlipSupply();
  const { epochsWithRotation, isLoading: epochsLoading } = useEpochsWithRotation({
    pollInterval: 60 * 1000,
  });

  const currentEpochWithRotation = epochsWithRotation?.[0];
  const previousEpochWithRotation = epochsWithRotation?.[1];
  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 lg:gap-6">
      <AuctionWidgetV2
        auction={auction}
        loading={epochsLoading}
        epochWithRotation={currentEpochWithRotation ?? null}
        previousEpochWithRotation={previousEpochWithRotation ?? null}
      />
      <div className="grid grid-cols-2 gap-6">
        <Card
          className="w-full"
          skeletonLineWidth={100}
          loading={isLoading}
          icon={<CoinsIcon className="text-cf-light-2" />}
          title="Bonded FLIP"
          tooltip={`The projected amount of ${FLIP_SYMBOL} which will be locked up for the entirety of the next Epoch.`}
          footer={<span>{formatUsdValue(auction?.totalBondedFlipValueUsd, false)}</span>}
          itemsCentered
        >
          {auction?.totalBondedFlip?.toFormatted('numeral')} {FLIP_SYMBOL}
        </Card>
        <Card
          className="w-full"
          skeletonLineWidth={100}
          loading={isLoading}
          icon={<MoneyBagIcon className="text-cf-light-2" />}
          title="Annual Rewards"
          tooltip={`The total amount of ${FLIP_SYMBOL} issued annually.`}
          footer={<span>{formatUsdValue(annualSystemCompoundedRewardsValueUsd, false)}</span>}
          itemsCentered
        >
          {annualSystemCompoundedRewards?.toFormatted('numeral')} {FLIP_SYMBOL}
        </Card>
        <Card
          className="w-full"
          skeletonLineWidth={100}
          loading={isLoading}
          icon={<GavelIcon className="text-cf-light-2" />}
          title="Minimum Active Bid"
          tooltip="MAB is the lowest winning bid if the auction were to resolve now."
          tooltipLink="https://docs.chainflip.io/validators/validator-auctions-bonds-and-rewards"
          footer={<span>{formatUsdValue(auction?.minActiveBidUSDC, false)}</span>}
          itemsCentered
        >
          {auction?.minActiveBid?.toFormatted('numeral')} {FLIP_SYMBOL}
        </Card>
        <Card
          className="w-full"
          skeletonLineWidth={100}
          loading={isLoading}
          icon={<NetworkIcon className="text-cf-light-2" />}
          title="Bidding Validators"
          tooltip="The total number of validators that have a 'Qualified' state."
          footer="&nbsp;"
          itemsCentered
        >
          {qualifiedNodes}
        </Card>
      </div>
    </div>
  );
};

export default AuctionStatPanels;
